/**
 * App router.
 *
 * Introduced alongside the installer flow. `/` keeps the original anonymous
 * field-intake survey (SurveyShell — extracted verbatim from the old App.vue
 * body) so the existing flow is undisturbed. Installer screens live under
 * `/installer/*` and are guarded by the installerAuth session so the login
 * surface never leaks into the public survey (plan §7.3).
 *
 * History mode: hash, so deep links work under Capacitor's file:// origin
 * without server-side rewrite config.
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';
import { useInstallerAuthStore } from '@/stores/installerAuth';
import { INSTALLER_ENABLED } from '@/config';

// Installer/field-test routes. Registered only when the feature flag is ON so
// the login surface (and its guard) doesn't exist at all while the flow is
// hidden — a never-registered route is safer than a registered-but-guarded one
// (an unknown /installer/* path falls through to the catch-all → survey root).
const installerRoutes: RouteRecordRaw[] = [
  {
    path: '/installer/login',
    name: 'installer-login',
    component: () => import('@/pages/InstallerLoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/installer',
    name: 'installer-home',
    component: () => import('@/pages/InstallerHomePage.vue'),
    meta: { requiresInstaller: true },
  },
  {
    // Pending field-test pumps — FCM only (legacy GetTestPendingPumps broker).
    // Contract workers have no legacy token; the page handles the empty/hidden
    // case itself. Guarded by the session like every installer route.
    path: '/installer/pending',
    name: 'installer-pending',
    component: () => import('@/pages/PendingPumpsPage.vue'),
    meta: { requiresInstaller: true, persona: 'fcm' },
  },
  {
    // New installation — available to both personas.
    path: '/installer/new',
    name: 'installer-new',
    component: () => import('@/pages/InstallationFormPage.vue'),
    meta: { requiresInstaller: true },
  },
  {
    // Field-test for a given installer record ref (FCM flow). `:ref` is the
    // new-app installer record ref returned by createInstallation.
    path: '/installer/field-test/:ref',
    name: 'installer-field-test',
    component: () => import('@/pages/FieldTestPage.vue'),
    meta: { requiresInstaller: true, persona: 'fcm' },
    props: true,
  },
  {
    // Installation checklist for a given ref (FCM flow). Kept as its own route
    // so it can be linked to directly; the field-test screen may also embed it.
    path: '/installer/checklist/:ref',
    name: 'installer-checklist',
    component: () => import('@/pages/ChecklistPage.vue'),
    meta: { requiresInstaller: true, persona: 'fcm' },
    props: true,
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'survey',
    component: () => import('@/pages/SurveyShell.vue'),
  },
  ...(INSTALLER_ENABLED ? installerRoutes : []),
  // Unknown paths fall back to the survey root.
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Guard installer routes: if there's no live session, attempt a restore once,
// then bounce to the login screen.
router.beforeEach(async (to) => {
  if (!to.meta.requiresInstaller) return true;
  const auth = useInstallerAuthStore();
  if (!auth.isAuthenticated) {
    // Try to rehydrate a persisted session before deciding.
    const ok = await auth.restore();
    if (!ok) return { name: 'installer-login' };
  }
  // Persona-gated routes (e.g. FCM-only field-test): bounce the wrong persona
  // back to the installer home rather than rendering an unusable screen.
  const required = to.meta.persona as string | undefined;
  if (required && auth.persona !== required) {
    return { name: 'installer-home' };
  }
  return true;
});

export default router;
